import React from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  Label,
  Row,
  Col,
  FormFeedback,
  FormGroup,
  Input,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useDispatch, useSelector } from "react-redux"
import { createReturnRequest } from "store/return-request/return-request-action"
import { getProvinceIns } from "store/province-in/province-in-action"

const ReturnModal = ({ isOpen, toggle, receiptData }) => {
  const dispatch = useDispatch()
  const { loading } = useSelector(state => state.returnRequest)

  const availableQty = receiptData?.in_qty || 0
  const consumedQty =
    (receiptData?.first_in_qty || 0) - (receiptData?.in_qty || 0)

  const validationSchema = Yup.object({
    return_quantity: Yup.number()
      .required(" Re-allocation quantity is required")
      .min(0.01, " Re-allocation quantity must be greater than 0")
      .max(availableQty, `Cannot exceed available quantity (${availableQty})`),
    province_note: Yup.string().max(1000, "Note is too long"),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      return_quantity: availableQty > 0 ? availableQty : "",
      province_note: "",
    },
    validationSchema,
    onSubmit: values => {
      handleSubmit(values)
    },
  })

  const handleSubmit = values => {
    const data = {
      province_in_id: receiptData?.unique_id,
      return_quantity: values.return_quantity,
      province_note: values.province_note || null,
    }

    dispatch(
      createReturnRequest(data, success => {
        if (success) {
          dispatch(getProvinceIns())
          toggle()
          formik.resetForm()
        }
      })
    )
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Return Items to Provider</ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Item</Label>
                <Input
                  type="text"
                  value={receiptData?.item?.ip_item_description || "-"}
                  disabled
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Batch Number</Label>
                <Input
                  type="text"
                  value={receiptData?.batch_number || "-"}
                  disabled
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Distributed Quantity</Label>
                <Input
                  type="text"
                  value={receiptData?.first_in_qty ?? "-"}
                  disabled
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Consumed / Distributed</Label>
                <Input type="text" value={consumedQty} disabled />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Available Return Quantity</Label>
                <Input type="text" value={availableQty} disabled />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Return Quantity *</Label>
                <Input
                  type="number"
                  name="return_quantity"
                  value={formik.values.return_quantity}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.return_quantity &&
                    !!formik.errors.return_quantity
                  }
                  min={0.01}
                  max={availableQty}
                  step="any"
                />
                {formik.touched.return_quantity &&
                  formik.errors.return_quantity && (
                    <FormFeedback>{formik.errors.return_quantity}</FormFeedback>
                  )}
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label>Note (optional)</Label>
                <Input
                  type="textarea"
                  name="province_note"
                  rows={3}
                  value={formik.values.province_note}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.province_note &&
                    !!formik.errors.province_note
                  }
                />
                {formik.touched.province_note &&
                  formik.errors.province_note && (
                    <FormFeedback>{formik.errors.province_note}</FormFeedback>
                  )}
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancel
          </Button>
          <Button
            color="warning"
            type="submit"
            disabled={loading || availableQty <= 0}
          >
            Submit Return Request
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default ReturnModal
